import React from 'react';
import { Award } from 'lucide-react';

const BADGE_STYLES = {
  gold: {
    gradient: 'from-yellow-400 to-amber-500',
    border: 'border-yellow-400',
    text: 'text-yellow-700',
    bg: 'bg-gradient-to-r from-yellow-50 to-amber-50',
    icon: '🥇'
  },
  silver: {
    gradient: 'from-gray-300 to-slate-400',
    border: 'border-gray-400',
    text: 'text-gray-700',
    bg: 'bg-gradient-to-r from-gray-50 to-slate-100',
    icon: '🥈'
  },
  bronze: {
    gradient: 'from-orange-400 to-amber-600',
    border: 'border-orange-400',
    text: 'text-orange-700',
    bg: 'bg-gradient-to-r from-orange-50 to-amber-50',
    icon: '🥉'
  }
};

// Full badge display for profile pages
export const TrustBadge = ({ tier, type = 'seller', size = 'medium' }) => {
  if (!tier) return null;
  
  const style = BADGE_STYLES[tier];
  if (!style) return null;
  
  const label = type === 'seller' ? 'Trusted Seller' : 'Trusted Tradie';
  const tierLabel = tier.charAt(0).toUpperCase() + tier.slice(1);
  
  const sizes = {
    small: 'px-2 py-1 text-xs',
    medium: 'px-3 py-1.5 text-sm',
    large: 'px-4 py-2 text-base'
  };
  
  return (
    <div 
      className={`inline-flex items-center gap-2 ${style.bg} ${style.border} border-2 rounded-full ${sizes[size]}`}
      data-testid={`trust-badge-${type}-${tier}`}
    >
      <span className="text-lg">{style.icon}</span>
      <div className="flex flex-col leading-tight">
        <span className={`font-bold ${style.text}`}>{label}</span>
        <span className={`text-xs ${style.text} opacity-80`}>Premium {tierLabel}</span>
      </div>
      <Award className={`h-5 w-5 ${style.text}`} />
    </div>
  );
};

// Compact badge for listings/cards
export const TrustBadgeCompact = ({ tier, type = 'seller' }) => {
  if (!tier) return null;
  
  const style = BADGE_STYLES[tier];
  if (!style) return null;
  
  const tierLabel = tier.charAt(0).toUpperCase() + tier.slice(1);
  
  return (
    <div 
      className={`inline-flex items-center gap-1 ${style.bg} ${style.border} border rounded-full px-2 py-0.5 text-xs`}
      data-testid={`trust-badge-compact-${tier}`}
    >
      <span>{style.icon}</span>
      <span className={`font-semibold ${style.text}`}>{tierLabel}</span>
    </div>
  );
};

// Badge progress indicator (for profile page)
export const BadgeProgress = ({ fiveStarCount, currentTier }) => {
  const tiers = [
    { name: 'Bronze', min: 10 },
    { name: 'Silver', min: 20 },
    { name: 'Gold', min: 30 }
  ];
  
  // Find next tier
  let nextTier = null;
  let progress = 0;
  
  if (fiveStarCount < 10) {
    nextTier = tiers[0];
    progress = (fiveStarCount / 10) * 100;
  } else if (fiveStarCount < 20) {
    nextTier = tiers[1];
    progress = ((fiveStarCount - 10) / 10) * 100;
  } else if (fiveStarCount < 30) {
    nextTier = tiers[2];
    progress = ((fiveStarCount - 20) / 10) * 100;
  } else {
    // Already at Gold
    return (
      <div className="text-center p-4 bg-yellow-50 rounded-lg border border-yellow-200">
        <span className="text-2xl">🏆</span>
        <p className="text-yellow-700 font-semibold mt-2">Maximum Tier Achieved!</p>
        <p className="text-yellow-600 text-sm">You've reached Gold status with {fiveStarCount} five-star reviews</p>
      </div>
    );
  }
  
  return (
    <div className="p-4 bg-stone-50 rounded-lg border border-stone-200">
      <div className="flex justify-between items-center mb-2">
        <span className="text-sm font-medium text-stone-700">
          Progress to {nextTier.name}
        </span>
        <span className="text-sm text-stone-500">
          {fiveStarCount}/{nextTier.min} ⭐ reviews
        </span>
      </div>
      <div className="w-full bg-stone-200 rounded-full h-3">
        <div 
          className={`h-3 rounded-full transition-all duration-500 ${
            nextTier.name === 'Gold' ? 'bg-gradient-to-r from-yellow-400 to-amber-500' :
            nextTier.name === 'Silver' ? 'bg-gradient-to-r from-gray-300 to-slate-400' :
            'bg-gradient-to-r from-orange-400 to-amber-600'
          }`}
          style={{ width: `${progress}%` }}
        />
      </div>
      <p className="text-xs text-stone-500 mt-2">
        {nextTier.min - fiveStarCount} more 5-star review{nextTier.min - fiveStarCount !== 1 ? 's' : ''} to unlock {nextTier.name} badge!
      </p>
    </div>
  );
};

export default TrustBadge;
